/**
 * SWISS School Portal — navigation model
 * ======================================
 * Single source of truth for the hybrid nav introduced by the
 * unified redesign:
 *
 *   top bar        → the `sections` below (always visible)
 *   left sub-rail  → the active section's `items` (desktop, and only
 *                    once you've drilled into a section)
 *   chip row       → the same `items` laid out horizontally (mobile)
 *   drawer         → sections + items together (mobile hamburger)
 *
 * A section with no `items` (Dashboard) is a leaf: it
 * navigates straight to `to` and renders no sub-rail, which is what
 * keeps the home screen uncluttered.
 *
 * Module gating is unchanged from the previous sidebar — `module`
 * keys are checked against the school's programme tier via
 * services/tiers, and locked entries render greyed with an upgrade
 * hint rather than disappearing.
 */

import {
  LayoutDashboard,
  GraduationCap,
  Users,
  Shield,
  Calendar,
  FileText,
  AlertTriangle,
  ClipboardList,
  AlertCircle,
  Activity,
  ShieldAlert,
  ShieldCheck,
  ClipboardCheck,
  Sparkles,
  FileCheck,
  Scale,
  HeartHandshake,
  Brain,
  BookOpen,
  Stethoscope,
  HelpingHand,
  UserPlus,
} from 'lucide-react';

/** Crisis support, quoted identically everywhere it appears. */
export const CRISIS_HOTLINE = '+91 80626 89525';
export const CRISIS_HOTLINE_TEL = '+918062689525';
export const SUPPORT_EMAIL = 'swiss@mysafespaces.org';

/**
 * School-facing sections, in top-bar order.
 * `tone: 'danger'` renders Crisis in the error hue per the design.
 */
export const SCHOOL_SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    to: '/dashboard',
    icon: LayoutDashboard,
    items: null,
  },
  {
    key: 'certification',
    label: 'Certification',
    icon: GraduationCap,
    items: [
      { path: '/progress', label: 'Certification Progress', icon: ClipboardList, module: 'progress' },
      { path: '/teachers', label: 'Teachers', icon: GraduationCap, module: 'teachers' },
      { path: '/labs', label: 'Teacher Labs', icon: Calendar, module: 'labs' },
      { path: '/sessions', label: 'In-person Sessions', icon: ClipboardCheck, module: 'sessions' },
      // SWISS Safe Hands staff onboarding — licensed per school.
      { path: '/safe-hands', label: 'Staff Onboarding', icon: UserPlus, module: 'safehands' },
    ],
  },
  {
    key: 'wellbeing',
    label: 'Wellbeing',
    icon: HeartHandshake,
    items: [
      { path: '/council', label: 'Wellness Council', icon: Users, module: 'council' },
      { path: '/anon-reports', label: 'Anonymous Reports', icon: ShieldAlert, module: 'anon' },
      { path: '/intellicare', label: 'Intellicare', icon: Sparkles, module: 'intellicare' },
      // Enabled per school by an MSS admin, not by programme tier.
      { path: '/assessments', label: 'Assessments', icon: ClipboardList, requiresAssessments: true },
      { path: '/semh', label: 'SSWS · Wellbeing Screen', icon: Brain, module: 'semh' },
      { path: '/ld', label: 'SSLS · Student Learning Screen', icon: BookOpen, module: 'ld' },
      { path: '/workshops', label: 'Workshops', icon: Calendar, module: 'workshops' },
    ],
  },
  {
    key: 'welltrack',
    label: 'SWISS WellTrack',
    icon: Stethoscope,
    items: [
      { path: '/scholarcare', label: 'Dashboard', icon: LayoutDashboard, module: 'scholarcare', exact: true },
      { path: '/scholarcare/students', label: 'Students', icon: Users, module: 'scholarcare' },
      { path: '/scholarcare/intake', label: 'Intake / EWQ', icon: ClipboardList, module: 'scholarcare' },
      { path: '/scholarcare/sessions', label: 'Sessions', icon: ClipboardCheck, module: 'scholarcare' },
      { path: '/scholarcare/referrals', label: 'Referrals', icon: FileCheck, module: 'scholarcare' },
      { path: '/scholarcare/reports', label: 'Reports', icon: FileText, module: 'scholarcare' },
      { path: '/scholarcare/crisis', label: 'Crisis Response', icon: AlertTriangle, module: 'scholarcare' },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    items: [
      { path: '/compliance', label: 'Compliance', icon: ShieldCheck, module: 'compliance' },
      { path: '/posh', label: 'POSH Compliance', icon: Scale, module: 'posh' },
      { path: '/pocso', label: 'POCSO Compliance', icon: HeartHandshake, module: 'pocso' },
      { path: '/audit', label: 'Audit Suite', icon: FileCheck, module: 'audit' },
      // CBSE mandatory public disclosure — not tier-gated; the page
      // itself explains when the school isn't CBSE-affiliated.
      { path: '/disclosure', label: 'Mandatory Disclosure', icon: FileText },
    ],
  },
  {
    key: 'crisis',
    label: 'Crisis',
    tone: 'danger',
    icon: AlertTriangle,
    items: [
      { path: '/crisis', label: 'Crisis Dashboard', icon: AlertTriangle, exact: true },
      { path: '/crisis/incidents', label: 'All Incidents', icon: AlertCircle },
      { path: '/crisis/report', label: 'Report Incident', icon: Activity },
      { path: '/crisis/protocols', label: 'Protocols', icon: Shield },
    ],
  },
  {
    key: 'resources',
    label: 'Resources',
    icon: FileText,
    items: [
      { path: '/resources', label: 'Resource Library', icon: BookOpen, module: 'resources', exact: true },
      // The Safe Hands walkthrough (ported from the public website). Open
      // to every school, licensed or not — it explains the programme.
      { path: '/resources/safe-hands', label: 'SWISS Safe Hands', icon: HelpingHand, module: 'resources' },
    ],
  },
];

/**
 * A third-party auditor sees only the Physical Audit module. No
 * `module` key, so it never renders locked.
 */
export const AUDITOR_SECTIONS = [
  {
    key: 'audit',
    label: 'Physical Audit',
    to: '/audit',
    icon: FileCheck,
    items: null,
  },
];

/**
 * Resolve the active section for a pathname.
 *
 * Matching is longest-prefix so that nested routes land on the right
 * section — /crisis/incidents/42 resolves to Crisis, /semh/screen to
 * Wellbeing. Returns undefined for routes outside the model (e.g.
 * /settings), which correctly renders no sub-rail.
 */
export function sectionForPath(sections, pathname) {
  let best;
  let bestLen = -1;

  for (const section of sections) {
    const candidates = section.items
      ? section.items.map((i) => i.path)
      : [section.to];

    for (const candidate of candidates) {
      if (!candidate) continue;
      const isMatch =
        pathname === candidate || pathname.startsWith(`${candidate}/`);
      if (isMatch && candidate.length > bestLen) {
        best = section;
        bestLen = candidate.length;
      }
    }
  }

  return best;
}
